import { formatTokenSymbol } from "@/lib/asset-naming";
import type { WalletBalanceItem } from "../types";
import type { PriceChange } from "./types";
import { withPercent } from "@/lib/percent";

/*
 * The insights' arithmetic, free of React so it is tested on its own. Nothing
 * here invents a figure: a change the backend has not computed yet (null) is
 * null all the way to the screen, which then leaves it out.
 */

type Lang = "fa" | "en";

/** "1.546" → 1.546 (percent). Null, empty or non-numeric → null. */
export function parseChange(raw: PriceChange | undefined): number | null {
  if (raw === null || raw === undefined || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export type Tone = "up" | "down" | "flat";
export const changeTone = (n: number): Tone => (n > 0 ? "up" : n < 0 ? "down" : "flat");

/** 1.546 → "+1.55%" / «+٪۱٫۵۵» (lib/percent); 0 → "0%". */
export function formatChange(n: number, lang: Lang): string {
  // Formatted as the number itself (never n / 100), so "1.555" reads the same wherever it comes from.
  const digits = n === 0 ? 0 : Math.abs(n) < 10 ? 2 : 1;
  const text = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US", { maximumFractionDigits: digits, minimumFractionDigits: digits, signDisplay: "exceptZero" }).format(n);
  return withPercent(text, lang === "fa");
}

/**
 * The change of a sum, from each part's value now and its own change: each
 * part was worth `value / (1 + change)` at the start, so the whole moved by
 * (now − then) / then. Known only when every part that holds value has a
 * change — one unknown part makes the whole unknown.
 */
export function combinedChange(parts: { value: number; change: number | null }[]): number | null {
  let now = 0;
  let then = 0;
  for (const p of parts) {
    if (!(p.value > 0)) continue;
    if (p.change === null) return null;
    now += p.value;
    then += p.value / (1 + p.change / 100);
  }
  // At the backend's own precision (three decimals), so one network reads exactly its own change.
  return then > 0 ? Math.round(((now - then) / then) * 100_000) / 1000 : null;
}

/** A whole-token decimal string → number (0 for anything absent or malformed). */
export const decimalOf = (raw: string | number | null | undefined) => {
  const n = Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** The locked and spendable parts of a list of tokens, each priced at its sell price (IRR). */
export function lockedSplit(assets: WalletBalanceItem[]) {
  let locked = 0;
  let spendable = 0;
  for (const a of assets) {
    const price = decimalOf(a.price_sell_irr);
    locked += decimalOf(a.locked) * price;
    spendable += decimalOf(a.spendable) * price;
  }
  return { locked, spendable, hasLocked: locked > 0 };
}

/** A token amount in the page's digits, up to six decimals. */
export const formatDecimal = (n: number, lang: Lang) => n.toLocaleString(lang === "fa" ? "fa-IR" : "en-US", { maximumFractionDigits: n !== 0 && Math.abs(n) < 1 ? 6 : 4 });

/** The fee's ticker. The backend still says "matic" for Polygon's coin, which is POL now. */
export const feeSymbol = (raw: string | null | undefined) => (String(raw ?? "").toLowerCase() === "matic" ? "POL" : formatTokenSymbol(raw));
